import { restHost } from '../../apiConfig';

export type CheckState = 'operational' | 'degraded' | 'down';

export interface CheckResult {
  name: string;
  state: CheckState;
  latencyMs: number | null;
  detail: string;
}

// restHost is a bare host:port — without an explicit scheme fetch would resolve it relative to the page.
const restBase = restHost.startsWith('http') ? restHost : `https://${restHost}`;

// Plain fetch on purpose: the shared axios client's 401 interceptor redirects to login, and this page treats 401 as healthy.
async function probe(
  name: string,
  path: string,
  isHealthy: (status: number) => boolean,
): Promise<CheckResult> {
  const started = performance.now();
  try {
    const res = await fetch(`${restBase}${path}`, {
      credentials: 'omit',
      cache: 'no-store',
    });
    const latencyMs = Math.round(performance.now() - started);
    if (isHealthy(res.status)) {
      return {
        name,
        state: 'operational',
        latencyMs,
        detail: `HTTP ${res.status}`,
      };
    }
    return {
      name,
      state: res.status >= 500 ? 'down' : 'degraded',
      latencyMs,
      detail: `HTTP ${res.status}`,
    };
  } catch {
    return { name, state: 'down', latencyMs: null, detail: 'unreachable' };
  }
}

export const runStatusChecks = (): Promise<CheckResult[]> =>
  Promise.all([
    probe('REST API', '/ping', (s) => s === 200),
    // 401 = the request reached auth middleware, proving the v1 mount routes.
    probe('API v1 mount', '/api/v1/customers', (s) => s === 401 || s === 200),
  ]);

export const overallState = (results: CheckResult[]): CheckState => {
  if (results.some((r) => r.state === 'down')) return 'down';
  if (results.some((r) => r.state === 'degraded')) return 'degraded';
  return 'operational';
};
